import { useEffect, useState } from 'react';
import { useAddItemsToCart, useCart } from 'hooks';
import classNames from 'classnames';
import { useSettings } from 'contexts/SettingContext';
import useProductAddToCartTracking from 'hooks/tracking/useProductAddToCartTracking';
import { useRouter } from 'next/router';
import { formatPriceByStoreLocale, getCurrencyCode } from 'lib/shopify';
import SiteLink from 'components/shared/sitelink/sitelink';
import { addLocaleToHref } from 'lib/utils/helpers';
import ItemCounter from '../../product/itemCounter';
import styles from './bundleCartSection.module.scss';

const BundleCartSection = ({
  className,
  outOfStock,
  resources,
  shopifyData,
  productBundleData,
  bundlePrices,
  locale,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const router = useRouter();

  const addItems = useAddItemsToCart();
  const { addProducts } = useSettings();
  const {
    freeItem,
    amountOfProductsToGetFreeItem,
    promoCollectionName,
    shopifyTagsToExcludeFromPromo,
  } = resources.secondpartsettings;
  const { products, bundleDiscount, title } = productBundleData;

  // This is to initialize the cart when the customer is navigating to a product page
  // eslint-disable-next-line
  const { cart } = useCart();

  const { addToCart, addingToCart, linkToAnotherProductButtonText, linkToAnotherProductMessage } =
    resources.productpage;

  const getButtonText = () => {
    if (isLoading) {
      return addingToCart?.text;
    }

    const bundlePrice = bundlePrices?.price;
    const bundleCompareAtPrice = bundlePrices?.compareAtPrice;

    const hasCompareAtPrice = bundleCompareAtPrice > 0;

    return (
      <div className={styles.cartButtonText}>
        {addToCart?.text}
        <span>-</span>
        <span className={styles.bundlePrice}>
          {hasCompareAtPrice && (
            <s>
              {formatPriceByStoreLocale({
                locale: router.locale,
                price: bundleCompareAtPrice,
              })}
            </s>
          )}
          {bundlePrice &&
            !hasCompareAtPrice &&
            `${formatPriceByStoreLocale({
              locale: router.locale,
              price: bundlePrice,
            })}`}
        </span>
        {hasCompareAtPrice && (
          <span className={styles.salesPrice}>
            {formatPriceByStoreLocale({
              locale: router.locale,
              price: bundlePrice,
            })}
          </span>
        )}
      </div>
    );
  };

  const handleAddToCart = async () => {
    setIsLoading(true);

    const allProducts = shopifyData.reduce((prev, shopifyProduct) => {
      const relatedProducts = shopifyProduct.relatedProducts || [];
      return [...prev, shopifyProduct.variantId, ...relatedProducts];
    }, []);

    let customAttributes = [];

    if (bundleDiscount) {
      const bundleId = shopifyData.reduce((acc, p) => acc + p.productId, '');

      customAttributes = [
        { key: '_bundle_discount', value: bundleDiscount.toString() },
        { key: '_bundle_id', value: bundleId },
        {
          key: '_bundle_message',
          value: `${title} -${bundleDiscount}%.`,
        },
      ];
    }

    const productsDataForCheckout = [...new Set(allProducts)].map(id => ({
      variantId: id,
      quantity,
      customAttributes,
    }));

    try {
      await addItems(productsDataForCheckout, freeItem?.product?.shopifyData, {
        amountOfProductsToGetFreeItem,
        promoCollectionName,
        shopifyTagsToExcludeFromPromo,
      });
    } catch (e) {
      // eslint-disable-next-line
      console.error(e);
    }

    products.map(item =>
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useProductAddToCartTracking({
        product: {
          name: item.title,
          id: item.shopifyData?.variants?.[0]?.product_id,
          category: products?.[0]?.title,
          price: item.shopifyData.variants?.[0]?.price,
          itemListName: productBundleData?.breadcrumbs?.breadcrumbEnd,
          productSpecsCollection: item.productSpecsCollection,
          quantity,
          currencyCode: getCurrencyCode(locale),
        },
      })
    );

    setIsLoading(false);

    const itemsDataForAddToCart = shopifyData
      .filter(product => !product.isFreeItem)
      .map(product => ({
        title: product.productTitle,
        slug: product.productHandle,
        variantId: product.variantId,
      }));
    addProducts(itemsDataForAddToCart);
  };

  useEffect(() => {
    setQuantity(1);
  }, [router.asPath]);

  if (
    productBundleData?.linkToAnotherProduct?.slug &&
    productBundleData?.useLinkToAnotherProduct !== false
  ) {
    return (
      <div className={classNames(className, styles.linkToAnotherProduct)}>
        <p>{linkToAnotherProductMessage?.text}</p>
        <SiteLink
          locale={locale}
          className={styles.linkToAnotherButton}
          style={{
            backgroundColor: resources?.btncolors?.linkToAnotherProductColor?.text,
            color: resources?.btncolors?.linkToAnotherProductTextColor?.text,
          }}
          href={addLocaleToHref(productBundleData?.linkToAnotherProduct?.slug, locale, router)}
        >
          {linkToAnotherProductButtonText?.text}
        </SiteLink>
      </div>
    );
  }

  return (
    <div className={classNames(className, styles.cartSection)}>
      <ItemCounter
        quantity={quantity}
        updateQuantity={value => setQuantity(value)}
        className={styles.itemCounter}
      />
      <button
        type="button"
        disabled={outOfStock}
        className={styles.addToCartButton}
        style={{
          backgroundColor: resources?.btncolors?.addToCartButtonColor?.text,
          color: resources?.btncolors?.addToCartButtonTextColor?.text,
        }}
        onClick={() => {
          handleAddToCart();
        }}
      >
        {getButtonText()}
      </button>
    </div>
  );
};

export default BundleCartSection;
